/**
 * Checkbox — Business app checkbox primitive.
 *
 * Native `<input type="checkbox">` wrapped in a styled box. Exposes a
 * Radix-compatible `onCheckedChange` API so components authored against
 * shadcn's checkbox signature (Lovable deliveries) can be used without
 * modification.
 *
 * a11y:
 *   - Visible focus ring (biz-ring)
 *   - 20px × 20px hit-box meets accessible target when wrapped in a larger
 *     clickable label (callers are expected to do this for touch scenarios)
 */
import { forwardRef, type InputHTMLAttributes } from 'react';
import { Check, Minus } from '@phosphor-icons/react';
import { cn } from '../utils/cn';

export type CheckedState = boolean | 'indeterminate';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'type' | 'checked'> {
  checked?: CheckedState;
  onCheckedChange?: (checked: CheckedState) => void;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, checked, onCheckedChange, disabled, ...rest }, ref) => {
    const isIndeterminate = checked === 'indeterminate';
    const isChecked = checked === true;

    return (
      <span
        className={cn(
          'relative inline-flex h-5 w-5 shrink-0 items-center justify-center',
          'rounded-[4px] border transition-colors duration-biz-fast',
          isChecked || isIndeterminate
            ? 'border-biz-primary bg-biz-primary text-biz-primary-foreground'
            : 'border-biz-input bg-biz-background',
          'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-biz-ring',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
      >
        <input
          ref={ref}
          type="checkbox"
          className="peer absolute inset-0 m-0 cursor-pointer appearance-none opacity-0 disabled:cursor-not-allowed"
          checked={isChecked}
          disabled={disabled}
          aria-checked={isIndeterminate ? 'mixed' : isChecked}
          onChange={(event) => {
            onCheckedChange?.(event.target.checked);
          }}
          {...rest}
        />
        {isIndeterminate ? (
          <Minus size={12} weight="bold" aria-hidden className="pointer-events-none" />
        ) : isChecked ? (
          <Check size={12} weight="bold" aria-hidden className="pointer-events-none" />
        ) : null}
      </span>
    );
  },
);
Checkbox.displayName = 'Checkbox';
